import {
    Amount,
    CurrencyType,
    GetClaimSubmittedReportRequest,
    GetExpectedRateReportRequest,
    GetGrossCollectionsReportRequest,
    GetPatientCollectionsReportRequest,
    GetPayerMixReportRequest,
    GetPracticeSummaryReportRequest,
    ProviderReportItem,
    ReportLabelType,
    SpanType,
} from '@enter/core';
import * as d3 from 'd3';
import _ from 'lodash';
import { payerNames, waitForNoReason } from './util';

interface IReportRequest {
    start: string;
    end: string;
    span: SpanType;
}

interface IItemGenerator {
    periods: number;
    generate: (type: ReportLabelType, amount: number, offset: number) => ProviderReportItem;
}

const parseStringDate = d3.utcParse('%Y-%m-%d');

const findInterval = (span: SpanType): d3.CountableTimeInterval => {
    switch (span) {
        case SpanType.MONTH:
            return d3.utcMonth;
        case SpanType.WEEKOFYEAR:
            return d3.utcWeek;
        case SpanType.DAYOFYEAR:
            return d3.utcDay;
        default:
            throw new Error(`Unknown SpanType: ${span}`);
    }
};
const createAmount = (amt: number): Amount => {
    return {
        amount: String(amt),
        currency: CurrencyType.USD,
    };
};
const parseReportRequest = async (req: IReportRequest): Promise<IItemGenerator> => {
    const interval = findInterval(req.span);
    const startDate = parseStringDate(req.start) as Date;
    const endDate = parseStringDate(req.end) as Date;
    const periods = interval.count(interval.floor(startDate), interval.ceil(endDate)) + 1;
    const createGroup = (i: number): string => {
        const currDate = interval.offset(startDate, i);
        const currFirst = d3.utcYear.floor(currDate);
        return `${currDate.getUTCFullYear()} - ${String(interval.count(currFirst, currDate) + 1)}}`;
    };
    await waitForNoReason(400);
    return {
        periods,
        generate: (type, amt, i): ProviderReportItem => ({
            label: type,
            amount: createAmount(amt),
            group: createGroup(i),
        }),
    };
};

export class MetricsGenerator {
    // metrics

    public generateClaimsSubmittedReport = async (
        request: GetClaimSubmittedReportRequest,
    ): Promise<ProviderReportItem[] | undefined> => {
        const { periods, generate } = await parseReportRequest(request);
        return _.flatten(
            _.times(periods, (i: number) => {
                const submitted = {
                    type: ReportLabelType.CLAIMSUBMITTED,
                    amount: _.random(0, 5),
                };
                return _.map([submitted], d => generate(d.type, d.amount, i));
            }),
        );
    };

    public generatePayerExpectedReport = async (
        request: GetExpectedRateReportRequest,
    ): Promise<ProviderReportItem[] | undefined> => {
        const { periods, generate } = await parseReportRequest(request);
        const target = _.random(0.08, 0.09); // adjustments / charges ~= 8%
        return _.flatten(
            _.times(periods, (i: number) => {
                const charge = { type: ReportLabelType.EXPECTED, amount: _.random(1, 1000) };
                const adjustments = { type: ReportLabelType.ADJUSTMENTS, amount: charge.amount * target };
                return _.map([charge, adjustments], d => generate(d.type, d.amount, i));
            }),
        );
    };

    public generateGrossCollectionReport = async (
        request: GetGrossCollectionsReportRequest,
    ): Promise<ProviderReportItem[] | undefined> => {
        const { periods, generate } = await parseReportRequest(request);
        const target = _.random(0.8, 0.9); // subtotal / charge ~= 80%
        return _.flatten(
            _.times(periods, (i: number) => {
                const payer = { type: ReportLabelType.PAYERPAID, amount: _.random(1, 1000) };
                const patient = { type: ReportLabelType.PATIENTPAID, amount: _.random(1, 1000) };
                const subtotal = _.sumBy([payer, patient], 'amount');
                const charge = { type: ReportLabelType.CHARGE, amount: subtotal / target };
                return _.map([payer, patient, charge], d => generate(d.type, d.amount, i));
            }),
        );
    };

    public generatePatientCollectionsReport = async (
        request: GetPatientCollectionsReportRequest,
    ): Promise<ProviderReportItem[] | undefined> => {
        const { periods, generate } = await parseReportRequest(request);
        return _.flatten(
            _.times(periods, (i: number) => {
                const responsible = { type: ReportLabelType.PATIENTRESPONSIBILITY, amount: _.random(1, 1000) };
                const paid = { type: ReportLabelType.PATIENTPAID, amount: _.random(1, 1000) };
                const refund = { type: ReportLabelType.PATIENTREFUND, amount: _.random(1, 1000) };
                return _.map([responsible, paid, refund], d => generate(d.type, d.amount, i));
            }),
        );
    };

    public generatePracticeSummaryReport = async (
        request: GetPracticeSummaryReportRequest,
    ): Promise<ProviderReportItem[] | undefined> => {
        const { periods, generate } = await parseReportRequest(request);
        return _.flatten(
            _.times(periods, (i: number) => {
                const adjustments = { type: ReportLabelType.ADJUSTMENTS, amount: _.random(100, 1000) };
                const payments = { type: ReportLabelType.PAYMENTS, amount: _.random(140, 1400) };
                const charges = {
                    type: ReportLabelType.CHARGE,
                    amount: _.sumBy([adjustments, payments], 'amount'),
                };
                return _.map([charges, adjustments, payments], d => generate(d.type, d.amount, i));
            }),
        );
    };

    public generatePayerMixReport = async (
        request: GetPayerMixReportRequest,
    ): Promise<ProviderReportItem[] | undefined> => {
        await waitForNoReason(400);
        const payers = _.sampleSize(payerNames, 10);
        return _.times(
            10,
            (i: number): ProviderReportItem => {
                return {
                    label: ReportLabelType.PAYERMIX,
                    amount: createAmount(_.random(1e3, 1e5)),
                    group: _.get(payers, i % _.size(payers)),
                };
            },
        );
    };
}
